/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	jpdProjectPageLoadPermissionsStart,
	jpdProjectPageLoadPermissionsEnd,
} from '@jira/polaris__component-metrics/src/jpdProjectPageLoad.tsx';
import { getCollectionContainerScope } from '@jira/polaris__component-permissions-store/src/getProjectContainerScope.tsx';
import type { NavigationView } from '@jira/polaris__component-navigation-store/src/NavigationView.types.tsx';
import { NavigationContainer } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { PermissionsContainer } from '@jira/polaris__component-permissions-store/src/PermissionsContainer.tsx';
import { useEntityLimits } from '@jira/polaris__component-entity-limits-store/src/useEntityLimitsByType.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { usePathParam } from '@atlassian/react-resource-router';
import { useNavigationViewRemote } from '../../../controllers/remotes/navigation/index.tsx';
import { PERMISSIONS_CACHE_TTL } from '../../constants.tsx';

const errorTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.roadmaps.with-data.error-title',
	defaultMessage: 'Something went wrong',
	description: 'The document title to show when there is generic error loading the data',
});

const errorDescription = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.roadmaps.with-data.error-description',
	defaultMessage: 'Refresh the page to try again.',
	description: 'Description of the error message when there is generic error loading the data',
});

type WithDataProps = {
	collectionUUID: string;
	children: React.ReactNode;
	onViewCreated: (view: NavigationView) => void;
};
// TODO: To be removed with jpd-enable-roadmaps-recents-fe
export const WithData = ({
	children,
	collectionUUID,
	onViewCreated,
}: WithDataProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const navigationViewRemote = useNavigationViewRemote();
	const [entityLimits] = useEntityLimits();
	const [selectedViewUUID] = usePathParam('resource');
	const [selectedCollectionUUID] = usePathParam('collectionUUID');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	const isActiveCollection = selectedCollectionUUID === collectionUUID;

	const onSuccess = useCallback(
		(action: string) => {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'jpdNavigationV4Action success', {
				action,
				type: 'collections-views',
				isActiveCollection,
			});
		},
		[createAnalyticsEvent, isActiveCollection],
	);

	const onFailure = useCallback(
		(error: Error, action: string) => {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'jpdNavigationV4Action',
				},
				attributes: {
					action,
					type: 'collections-views',
				},
			});
			showFlag({
				id: `${action}-failed`,
				type: 'error',
				title: formatMessage(errorTitle),
				description: formatMessage(errorDescription),
				// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
				messageId:
					'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.ui.with-data.show-flag.error',
				messageType: 'transactional',
			});
		},
		[formatMessage, showFlag],
	);

	if (!navigationViewRemote) {
		return null;
	}

	return (
		<PermissionsContainer
			scope={getCollectionContainerScope(collectionUUID)}
			cacheTTL={PERMISSIONS_CACHE_TTL}
			isCollectionView
			isSharedView={false}
			isEmbeddedView={false}
			onLoadStart={jpdProjectPageLoadPermissionsStart}
			onLoadEnd={jpdProjectPageLoadPermissionsEnd}
		>
			<NavigationContainer
				isGlobal
				containerType="COLLECTION"
				activeViewId={selectedViewUUID}
				isActiveContainer={isActiveCollection}
				navigationRemote={navigationViewRemote}
				cloudId={cloudId}
				containerId={collectionUUID}
				entityLimits={entityLimits}
				onSuccess={onSuccess}
				onFailure={onFailure}
				onViewCreated={onViewCreated}
				createAnalyticsEvent={createAnalyticsEvent}
			>
				{children}
			</NavigationContainer>
		</PermissionsContainer>
	);
};
